import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { reverse } from 'named-urls'
import { useHistory } from 'react-router-dom'
import { isEmpty, size, some } from 'lodash'
import { FileText as FileTextIcon } from 'react-feather'
import { Box, Typography } from '@material-ui/core'

import { DocumentTemplateAction, DocumentTemplateDialog } from './components'

import helpers from 'helpers'

import { routes } from 'Routes'
import theme from 'theme'
import useStyles from './styles'

const DocumentTemplateItem = ({
  documentTemplate,
  evidenceId,
  governanceProjectId,
  activityId,
  linkedPrivacyPolicyIds = [],
  disabledInputs = false,
  onLink = () => {},
}) => {
  const classes = useStyles()
  const history = useHistory()

  const [openDialog, setOpenDialog] = useState(false)

  const companyPrivacyPolicies = documentTemplate?.companyPrivacyPolicies || []

  const hasLinkedPrivacyPolicy = some(companyPrivacyPolicies, (privacyPolicy) =>
    linkedPrivacyPolicyIds.includes(privacyPolicy?.id),
  )

  // A navegação vive aqui porque tanto a ação da linha (quando a organização ainda não tem documento
  // algum) quanto o "Criar novo documento" do modal levam para o mesmo lugar.
  //
  // A perda de alterações não salvas é confirmada pelo <Prompt> do ActivityManageForm, que cobre
  // qualquer transição do react-router — inclusive esta.
  const handleCreateDocument = () => {
    // Guarda além do botão desabilitado: sair para o fluxo de criação só faz sentido se o vínculo
    // puder acontecer no fim dele. Com a evidência bloqueada o documento seria gerado e o vínculo
    // automático recusado pelo backend, deixando o usuário com um documento solto.
    if (disabledInputs) return

    history.push({
      pathname: reverse(routes.documents.generate, {
        documentTemplateId: documentTemplate?.companyDocumentTemplateId,
      }),
      search: helpers.privacyPolicies.mountControlOriginSearch({
        governanceProjectId,
        activityId,
        evidenceId,
      }),
    })
  }

  const mountSummary = () => {
    if (hasLinkedPrivacyPolicy) return 'Vinculado como evidência'

    if (isEmpty(companyPrivacyPolicies)) {
      return 'Nenhum documento criado na organização'
    }

    return size(companyPrivacyPolicies) === 1
      ? '1 documento criado na organização'
      : `${size(companyPrivacyPolicies)} documentos criados na organização`
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      gridGap={theme.spacing(2)}
      px={2}
      py={1}
    >
      <Box display="flex" alignItems="center" gridGap={theme.spacing(1)}>
        <FileTextIcon size={20} />
        <Box>
          <Typography>{documentTemplate?.documentTemplate?.name}</Typography>
          <Typography
            variant="caption"
            color="secondary"
            className={hasLinkedPrivacyPolicy ? classes.linkedSummary : ''}
          >
            {mountSummary()}
          </Typography>
        </Box>
      </Box>
      <DocumentTemplateAction
        companyPrivacyPolicies={companyPrivacyPolicies}
        companyDocumentTemplateId={documentTemplate?.companyDocumentTemplateId}
        disabledInputs={disabledInputs}
        onOpenDialog={() => setOpenDialog(true)}
        onCreateDocument={handleCreateDocument}
      />
      {openDialog && (
        <DocumentTemplateDialog
          open={openDialog}
          setOpen={setOpenDialog}
          documentTemplateName={documentTemplate?.documentTemplate?.name}
          companyPrivacyPolicies={companyPrivacyPolicies}
          companyDocumentTemplateId={
            documentTemplate?.companyDocumentTemplateId
          }
          evidenceId={evidenceId}
          linkedPrivacyPolicyIds={linkedPrivacyPolicyIds}
          disabledInputs={disabledInputs}
          onLink={onLink}
          onCreateDocument={handleCreateDocument}
        />
      )}
    </Box>
  )
}

DocumentTemplateItem.propTypes = {
  documentTemplate: PropTypes.object.isRequired,
  evidenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  governanceProjectId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  activityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  linkedPrivacyPolicyIds: PropTypes.array,
  disabledInputs: PropTypes.bool,
  onLink: PropTypes.func,
}

export default DocumentTemplateItem
